import { Deferred, Link, router, usePage } from "@inertiajs/react";
import { Clock, ExternalLink, Loader2, Package } from "lucide-react";
import { useEffect, useMemo } from "react";

import StatusBadge from "@/components/request-status/status-badge";
import { ScrollArea } from "@/components/ui/scroll-area";
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetHeader,
  SheetTitle,
} from "@/components/ui/sheet";
import { formatCurrency } from "@/helpers/currency-formatter";
import { purchaseRequestPath } from "@/routes";
import type { PurchaseHistoryEntry, RequestItem, SharedData } from "@/types";
import { formatDateAR } from "@/utils/date";

interface PurchaseHistorySheetProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  requestItemId?: number | null;
  requestItemDescription?: string | null;
}

export default function PurchaseHistorySheet({
  open,
  onOpenChange,
  requestItemId,
  requestItemDescription,
}: PurchaseHistorySheetProps) {
  const page = usePage();
  type PageProps = SharedData & {
    purchase_history_by_request_item_id?: Record<number, PurchaseHistoryEntry[]>;
    request_items?: RequestItem[];
  };
  const pageProps = page.props as PageProps;

  // Trigger loading of deferred prop when opening if missing for current item
  useEffect(() => {
    if (!open || !requestItemId) return;

    const existing = pageProps.purchase_history_by_request_item_id?.[requestItemId];
    if (!existing) {
      router.reload({ only: ["purchase_history_by_request_item_id"] });
    }
  }, [open, requestItemId, pageProps.purchase_history_by_request_item_id]);

  const entries: PurchaseHistoryEntry[] = useMemo(() => {
    if (!pageProps.purchase_history_by_request_item_id || !requestItemId) return [];
    return pageProps.purchase_history_by_request_item_id[requestItemId] || [];
  }, [pageProps.purchase_history_by_request_item_id, requestItemId]);

  const visibleEntries = useMemo(() => entries.slice(0, 10), [entries]);

  const hasProductAssigned = useMemo(() => {
    const ris = pageProps.request_items;
    const ri = Array.isArray(ris) ? ris.find((r) => r.id === requestItemId) : undefined;
    return Boolean(ri?.product_id);
  }, [pageProps.request_items, requestItemId]);

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent side="right" className="sm:max-w-md">
        <SheetHeader>
          <SheetTitle className="tracking-tight">Historial de compras</SheetTitle>
          <SheetDescription className="leading-relaxed">
            Últimas compras del producto
          </SheetDescription>
        </SheetHeader>

        {/* Producto destacado para contexto visual claro */}
        {requestItemDescription ? (
          <div className="px-5 pt-2 pb-3">
            <div className="flex items-start gap-3 rounded-md border bg-card p-3">
              <Package className="h-4 w-4 text-muted-foreground mt-0.5" aria-hidden />
              <div className="min-w-0">
                <div className="text-[11px] uppercase tracking-wide text-muted-foreground">Producto</div>
                <div title={requestItemDescription} className="text-base font-semibold leading-snug text-foreground truncate">
                  {requestItemDescription}
                </div>
              </div>
            </div>
          </div>
        ) : null}

        {/* Wait for deferred prop before rendering list */}
        <Deferred
          data="purchase_history_by_request_item_id"
          fallback={
            <div className="px-5 py-6 text-sm text-muted-foreground flex items-center gap-2">
              <Loader2 className="h-4 w-4 animate-spin" aria-hidden />
              Cargando historial…
            </div>
          }
        >
          <div className="relative h-[500px]">
            <ScrollArea className="h-full w-full" style={{ scrollbarGutter: 'stable' }}>
            <div className="px-5 pb-6 pt-1 space-y-3">
              {(!entries || entries.length === 0) ? (
                <div className="text-sm text-muted-foreground">
                  {hasProductAssigned ? 'Sin compras previas' : 'Asigna un producto para ver historial'}
                </div>
              ) : (
                visibleEntries.map((e, idx) => {
                  const dateStr = e.created_at ? formatDateAR(e.created_at) : "";
                  const hasUsd = (e.currency || "").toUpperCase() === "USD";
                  return (
                    <div
                      key={`${e.purchase_request_id}-${idx}`}
                      className="group grid grid-cols-[1fr_auto] items-start gap-3 rounded-md border bg-card p-3 transition-colors hover:bg-muted/40"
                    >
                      <div className="min-w-0 space-y-1.5">
                        <div className="flex items-center gap-2 text-xs text-muted-foreground">
                          <Clock className="h-3.5 w-3.5" aria-hidden />
                          <span>{dateStr}</span>
                          <span className="opacity-40">•</span>
                          <StatusBadge status={e.purchase_request_status} />
                        </div>
                        <div className="text-sm font-medium tracking-tight text-foreground truncate">
                          {e.supplier_name}
                        </div>
                        <div className="text-xs text-muted-foreground">
                          <span className="font-medium text-foreground">
                            {typeof e.unit_price_ars === "number" && isFinite(e.unit_price_ars) && e.unit_price_ars > 0
                              ? formatCurrency(e.unit_price_ars, "ARS")
                              : formatCurrency(e.unit_price, e.currency || "ARS")}
                          </span>
                          {hasUsd && typeof e.unit_price === "number" && isFinite(e.unit_price) && (
                            <span> ({formatCurrency(e.unit_price, "USD")})</span>
                          )}
                          {e.quantity ? (
                            <span> • {e.quantity} {e.unit ?? ""}</span>
                          ) : null}
                        </div>
                      </div>
                      <div className="flex items-center gap-2 flex-shrink-0">
                        <Link
                          href={purchaseRequestPath(e.purchase_request_id)}
                          className="inline-flex items-center gap-1 text-xs text-primary hover:underline"
                          aria-label={`Ver solicitud #${e.purchase_request_number}`}
                        >
                          #{e.purchase_request_number}
                          <ExternalLink className="h-3.5 w-3.5 opacity-80 group-hover:opacity-100 transition-opacity" aria-hidden />
                        </Link>
                      </div>
                    </div>
                  );
                })
              )}
            </div>
            </ScrollArea>
          </div>
        </Deferred>
      </SheetContent>
    </Sheet>
  );
}
